import { useState } from "react";
import { toast } from "sonner";
import { Download, Upload, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { adminApi, apiError, money, openFile } from "@/lib/api";
import { REQUEST_STATUS, catName } from "@/data/fieldServices";
import { Panel, Labeled, inputCls } from "./AdminUI";

const Info = ({ label, children }) => <div><p className="text-xs uppercase tracking-wide text-slate-400">{label}</p><div className="text-sm text-[#0B132B]">{children || "-"}</div></div>;

const Files = ({ title, files }) => (
  <div><p className="text-xs uppercase tracking-wide text-slate-400">{title}</p>
    <div className="mt-1 flex flex-wrap gap-2">{(files || []).map((f) => (
      <button key={f.id} type="button" onClick={() => openFile(f.id).catch(() => toast.error("Could not open"))} className="rounded-full border border-slate-200 px-3 py-1 text-xs hover:border-gold">{f.original_filename}</button>
    ))}{!files?.length && <span className="text-xs text-slate-400">None</span>}</div>
  </div>
);

const InvoiceForm = ({ r, onCreated }) => {
  const [items, setItems] = useState([{ description: r.service_name, amount: r.quote_amount || r.estimate?.total || 0 }]);
  const [due, setDue] = useState("");
  const create = async () => {
    try {
      const { data } = await adminApi.post("/admin/invoices", { request_number: r.request_number, items: items.map((i) => ({ ...i, amount: Number(i.amount) })), due_date: due });
      toast.success(`Invoice ${data.invoice_number} sent`);
      onCreated();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  return (
    <div className="rounded-xl border border-slate-200 p-4" data-testid="invoice-form">
      <p className="text-sm font-semibold">Create invoice</p>
      {items.map((it, i) => (
        <div key={i} className="mt-2 flex gap-2">
          <input className={inputCls} value={it.description} onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))} data-testid={`invoice-desc-${i}`} />
          <input type="number" min="0" className={`${inputCls} w-28`} value={it.amount} onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))} data-testid={`invoice-amount-${i}`} />
          <button type="button" onClick={() => setItems(items.filter((_, j) => j !== i))} className="text-slate-400"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <button type="button" onClick={() => setItems([...items, { description: "", amount: 0 }])} className="inline-flex items-center gap-1 text-sm text-gold"><Plus className="h-4 w-4" /> Line</button>
        <input type="date" className={`${inputCls} w-44`} value={due} onChange={(e) => setDue(e.target.value)} />
        <button type="button" onClick={create} data-testid="invoice-create" className="rounded-full bg-[#0B132B] px-4 py-2 text-sm font-semibold text-white">Create & email invoice</button>
      </div>
    </div>
  );
};

export const RequestDialog = ({ r, onClose, onUpdated }) => {
  const [f, setF] = useState({ status: r.status, quote_amount: r.quote_amount ?? "", scheduled_date: r.scheduled_date, scheduled_time: r.scheduled_time, admin_notes: r.admin_notes || "", customer_message: "", notify: true });
  const save = async () => {
    try {
      const body = { ...f, quote_amount: f.quote_amount === "" ? null : Number(f.quote_amount) };
      onUpdated((await adminApi.patch(`/admin/requests/${r.request_number}`, body)).data);
      toast.success("Request updated");
      setF({ ...f, customer_message: "" });
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const upload = async (kind, file) => {
    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("file", file);
    try {
      onUpdated((await adminApi.post(`/admin/requests/${r.request_number}/files`, fd)).data);
      toast.success("Uploaded");
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="request-dialog">
        <DialogHeader><DialogTitle><span className="font-mono-x">{r.request_number}</span> · {r.service_name}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-3">
          <Info label="Customer">{r.full_name}{r.company ? ` · ${r.company}` : ""}<br /><a className="underline" href={`mailto:${r.email}`}>{r.email}</a><br /><a className="underline" href={`tel:${r.phone}`}>{r.phone}</a></Info>
          <Info label="Property">{r.address}, {r.city}, {r.state} {r.zip}<br />{r.property_type} · {r.occupancy}<br />{r.in_service_area ? r.service_area : <span className="text-red-600">Outside service area</span>}</Info>
          <Info label="Request">{catName(r.category)}<br />Preferred: {r.preferred_date || "-"} {r.preferred_time}<br />Interior: {r.interior_access ? "Yes" : "No"} · Contact: {r.contact_required ? "Yes" : "No"}<br />{r.urgency} · {r.num_properties} propert{r.num_properties > 1 ? "ies" : "y"}</Info>
          <Info label="Instructions">{r.instructions}</Info>
          <Info label="Project">{r.project_size} {r.additional_services && `· Extra: ${r.additional_services}`}</Info>
          <Info label="Estimate">{r.estimate?.total != null ? money(r.estimate.total) : "Quote"}</Info>
        </div>
        <Files title="Customer attachments" files={r.attachments} />
        <div className="grid gap-3 rounded-xl bg-[#FAF8F1] p-4 sm:grid-cols-2">
          <Labeled label="Status"><select className={inputCls} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="req-status-select">
            {Object.entries(REQUEST_STATUS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Labeled>
          <Labeled label="Quote amount ($)"><input type="number" min="0" className={inputCls} value={f.quote_amount} onChange={(e) => setF({ ...f, quote_amount: e.target.value })} data-testid="req-quote-amount" /></Labeled>
          <Labeled label="Scheduled date"><input type="date" className={inputCls} value={f.scheduled_date} onChange={(e) => setF({ ...f, scheduled_date: e.target.value })} data-testid="req-sched-date" /></Labeled>
          <Labeled label="Scheduled time"><input type="time" className={inputCls} value={f.scheduled_time} onChange={(e) => setF({ ...f, scheduled_time: e.target.value })} data-testid="req-sched-time" /></Labeled>
          <Labeled label="Message to customer (with status change)" className="sm:col-span-2"><input className={inputCls} value={f.customer_message} onChange={(e) => setF({ ...f, customer_message: e.target.value })} data-testid="req-customer-message" /></Labeled>
          <Labeled label="Internal notes" className="sm:col-span-2"><textarea rows={2} className={inputCls} value={f.admin_notes} onChange={(e) => setF({ ...f, admin_notes: e.target.value })} data-testid="req-admin-notes" /></Labeled>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.notify} onChange={(e) => setF({ ...f, notify: e.target.checked })} data-testid="req-notify" /> Email customer on status change</label>
          <button type="button" onClick={save} data-testid="req-save" className="justify-self-end rounded-full bg-[#0B132B] px-5 py-2 text-sm font-semibold text-white">Save</button>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {[["reports", "Inspection reports"], ["photos", "Photographs"]].map(([k, label]) => (
            <div key={k} className="space-y-2">
              <Files title={label} files={r[k]} />
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-dashed border-slate-400 px-4 py-2 text-xs"><Upload className="h-3 w-3" /> Upload {k === "reports" ? "report" : "photo"}
                <input type="file" className="hidden" data-testid={`upload-${k}`} onChange={(e) => e.target.files[0] && upload(k, e.target.files[0])} /></label>
            </div>
          ))}
        </div>
        <InvoiceForm key={r.quote_amount ?? "none"} r={r} onCreated={onClose} />
        <ol className="space-y-1 text-xs text-slate-500">{r.history.map((h, i) => <li key={i}>{h.at.slice(0, 16).replace("T", " ")} · <b>{REQUEST_STATUS[h.status]}</b> · {h.message}{h.by ? ` · ${h.by}` : ""}</li>)}</ol>
      </DialogContent>
    </Dialog>
  );
};

export const RequestsPanel = ({ requests, onOpen }) => {
  const [status, setStatus] = useState("");
  const exportCsv = async () => {
    const r = await adminApi.get("/admin/requests/export.csv", { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = "service-requests.csv";
    a.click();
  };
  const rows = requests.filter((r) => !status || r.status === status);
  return (
    <Panel title="Service requests" action={<button type="button" onClick={exportCsv} data-testid="export-csv" className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm"><Download className="h-4 w-4" /> Export CSV</button>}>
      <div className="mb-4 flex flex-wrap gap-2">
        {[["", "All"], ...Object.entries(REQUEST_STATUS)].map(([k, l]) => (
          <button key={k || "all"} type="button" onClick={() => setStatus(k)} data-testid={`req-filter-${k || "all"}`}
            className={`rounded-full px-3 py-1.5 text-xs font-medium ${status === k ? "bg-[#0B132B] text-white" : "bg-[#F5F3EC] text-slate-600"}`}>{l} {requests.filter((r) => !k || r.status === k).length}</button>
        ))}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] text-left text-sm" data-testid="requests-table">
          <thead className="text-xs uppercase text-slate-400"><tr><th className="py-2">Request</th><th>Submitted</th><th>Service</th><th>Customer</th><th>Property</th><th>Status</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.request_number} onClick={() => onOpen(r)} data-testid={`request-row-${r.request_number}`} className="cursor-pointer border-t border-slate-100 hover:bg-[#FAF8F1]">
                <td className="py-3 font-mono-x text-xs">{r.request_number}</td><td>{r.created_at.slice(0, 10)}</td><td>{r.service_name}</td>
                <td>{r.full_name}<br /><span className="text-xs text-slate-400">{r.company}</span></td><td>{r.city} {r.zip}</td><td>{REQUEST_STATUS[r.status]}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={6} className="py-8 text-center text-slate-400">No requests.</td></tr>}
          </tbody>
        </table>
      </div>
    </Panel>
  );
};

export const MetricsBar = ({ m }) => (
  <div className="grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="admin-metrics">
    {[["new_requests", "New requests"], ["scheduled", "Scheduled"], ["completed", "Completed"], ["pending_reports", "Pending reports"], ["monthly_revenue", "Revenue this month"]].map(([k, l]) => (
      <div key={k} className="rounded-2xl border border-slate-200 bg-white p-4">
        <p className="text-xs uppercase tracking-wide text-slate-400">{l}</p>
        <p data-testid={`metric-${k}`} className="mt-1 font-display text-2xl font-bold text-[#0B132B]">{k === "monthly_revenue" ? money(m?.[k] ?? 0) : m?.[k] ?? "–"}</p>
      </div>
    ))}
  </div>
);

export const ClientsPanel = ({ clients }) => (
  <Panel title="Clients">
    <table className="w-full text-left text-sm" data-testid="clients-table">
      <thead className="text-xs uppercase text-slate-400"><tr><th className="py-2">Name</th><th>Company</th><th>Email</th><th>Phone</th><th>Requests</th><th>Since</th></tr></thead>
      <tbody>{clients.map((c) => <tr key={c.user_id} className="border-t border-slate-100"><td className="py-2">{c.name}</td><td>{c.company}</td><td>{c.email}</td><td>{c.phone}</td><td>{c.request_count}</td><td>{c.created_at?.slice(0, 10)}</td></tr>)}
        {!clients.length && <tr><td colSpan={6} className="py-6 text-center text-slate-400">No client accounts yet.</td></tr>}</tbody>
    </table>
  </Panel>
);

export const InvoicesPanel = ({ invoices }) => (
  <Panel title="Invoices">
    <table className="w-full text-left text-sm" data-testid="invoices-table">
      <thead className="text-xs uppercase text-slate-400"><tr><th className="py-2">Invoice</th><th>Request</th><th>Customer</th><th>Total</th><th>Status</th><th>Created</th></tr></thead>
      <tbody>{invoices.map((i) => <tr key={i.invoice_number} className="border-t border-slate-100"><td className="py-2 font-mono-x text-xs">{i.invoice_number}</td><td>{i.request_number}</td><td>{i.full_name}</td><td>{money(i.total)}</td><td className={i.status === "paid" ? "text-emerald-700" : ""}>{i.status}</td><td>{i.created_at.slice(0, 10)}</td></tr>)}
        {!invoices.length && <tr><td colSpan={6} className="py-6 text-center text-slate-400">No invoices yet.</td></tr>}</tbody>
    </table>
  </Panel>
);
